import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/api";
import { CreditCard, Loader2, AlertCircle, ExternalLink } from "lucide-react";

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Get userId from localStorage (stored directly as a string)
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.post(`/payments/by-userid`, {
          userId,
        });
        setPayments(res.data.data || []);
      } catch (err) {
        setError("Failed to fetch payments.");
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchPayments();
    else setLoading(false);
  }, [userId]);

  return (
    <div className="pt-24 min-h-screen bg-gray-50 py-10 px-4 flex flex-col items-center">
      <div className="max-w-2xl w-full">
        <h1 className="text-3xl font-bold text-[#1A0F3C] mb-8 flex items-center gap-3">
          <CreditCard className="w-8 h-8 text-[#FF4D12]" />
          My Payments
        </h1>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-[#FF4D12] animate-spin mb-4" />
            <span className="text-gray-600">Loading payments...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <AlertCircle className="w-10 h-10 text-red-500 mb-4" />
            <span className="text-red-600">{error}</span>
          </div>
        ) : payments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <CreditCard className="w-16 h-16 text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              No Payments Found
            </h2>
            <p className="text-gray-500 mb-4 text-center">
              You haven't made any payments yet. Your payments will appear here
              once you make a booking.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-6 py-2 rounded-lg bg-[#FF4D12] text-white hover:bg-[#FF3D00] transition-colors font-semibold shadow"
            >
              Go to Dashboard
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {payments.map((payment) => (
              <div
                key={payment._id}
                className="bg-white rounded-xl shadow p-6 flex flex-col gap-2"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                  <div>
                    <div className="text-lg font-semibold text-[#1A0F3C] mb-1 flex items-center gap-2">
                      {payment.metadata?.packageTitle || "Event Payment"}
                      {payment.invoiceLink && (
                        <a
                          href={payment.invoiceLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-[#FF4D12] hover:underline flex items-center gap-1 text-sm"
                        >
                          Invoice <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mb-1">
                      Order ID:{" "}
                      <span className="font-mono">
                        {payment.orderId || payment._id}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      Date:{" "}
                      {payment.createdAt
                        ? new Date(payment.createdAt).toLocaleString()
                        : "N/A"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#FF4D12]">
                      {payment.currency || "INR"}{" "}
                      {payment.amount ? payment.amount.toLocaleString() : "N/A"}
                    </div>
                    <div
                      className={`text-xs mt-1 px-2 py-1 rounded-full ${payment.paymentStatus === "SUCCESS" ? "bg-green-100 text-green-700" : payment.paymentStatus === "FAILED" ? "bg-red-100 text-red-700" : payment.paymentStatus === "PENDING" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-700"}`}
                    >
                      {payment.paymentStatus}
                    </div>
                  </div>
                </div>
                {payment.customerDetails && (
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500 border-t pt-2">
                    <div>
                      <span className="font-semibold text-[#1A0F3C]">
                        Name:
                      </span>{" "}
                      {payment.customerDetails.name}
                    </div>
                    <div>
                      <span className="font-semibold text-[#1A0F3C]">
                        Email:
                      </span>{" "}
                      {payment.customerDetails.email}
                    </div>
                    <div>
                      <span className="font-semibold text-[#1A0F3C]">
                        Phone:
                      </span>{" "}
                      {payment.customerDetails.phone}
                    </div>
                  </div>
                )}
                {payment.metadata && (
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500 border-t pt-2">
                    <div>
                      <span className="font-semibold text-[#1A0F3C]">
                        Budget:
                      </span>{" "}
                      {payment.metadata.packageBudget}
                    </div>
                    <div>
                      <span className="font-semibold text-[#1A0F3C]">
                        Original Amount:
                      </span>{" "}
                      {payment.metadata.originalAmount}
                    </div>
                    <div>
                      <span className="font-semibold text-[#1A0F3C]">
                        Discount:
                      </span>{" "}
                      {payment.metadata.discountPercentage}%
                    </div>
                    <div>
                      <span className="font-semibold text-[#1A0F3C]">
                        Custom Amount:
                      </span>{" "}
                      {payment.metadata.isCustomAmount === "true"
                        ? "Yes"
                        : "No"}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Payments;
